import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Table, Spin, Alert, Input, Space, Typography, Row, Col, Card, Button, Select, InputNumber, Popconfirm, message, ConfigProvider, theme as antdTheme, Tooltip } from 'antd';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { LeftOutlined, RightOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { VariableSizeList, FixedSizeList } from 'react-window'; // Import FixedSizeList
import { useAnalyses, updateAnalysis, createAnalysis, deleteAnalysis } from '../Lookup_components/Analysis_lookup';
import { useAnalysisSamples, invalidateSampleCache } from '../Lookup_components/Sample_lookup';
import { useDomainValues, searchParameters } from '../Lookup_components/document_domainvalueslookup';
import debounce from 'lodash/debounce';
import React from 'react';
import { useTheme } from '../../ThemeContext';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../supabaseClient'; // Import Supabase client

const { Title, Text } = Typography;
const { Option } = Select;

// --- Virtualized Listbox for Autocomplete --- 

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  
  // Combine react-window style with necessary overrides for MUI list item
  const combinedStyle = {
    ...style, 
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
    padding: 0, 
    display: 'flex',
    alignItems: 'center'
  };

  // Render children directly if it's just a string (like loading/no options)
  if (typeof dataSet === 'string') {
    return <li style={combinedStyle} key={index}>{dataSet}</li>;
  }

  // Check if dataSet has props and key (typical MUI list item structure)
  if (dataSet && dataSet.props && dataSet.key) {
    return React.cloneElement(dataSet, {
        style: { 
          ...combinedStyle, 
          paddingLeft: '8px',
          paddingRight: '8px'
        },
    });
  }

  // Fallback if structure is unexpected
  return <li style={combinedStyle} key={index}>{JSON.stringify(dataSet)}</li>;
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = [];
  React.Children.forEach(children, (item) => {
     if (React.isValidElement(item)) {
        itemData.push(item);
     } else if (typeof item === 'string') {
         itemData.push(item);
     }
  });

  const itemCount = itemData.length;
  const itemSize = 40; // VERLAAGD: Maak items iets compacter
  const maxHeight = 8 * itemSize; // Max hoogte voor 8 items

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList 
          itemData={itemData}
          height={itemCount > 8 ? maxHeight : Math.max(itemSize, itemCount * itemSize)} 
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
// --- End Virtualized Listbox --- 

// --- Virtualized Table Body --- 
const VIRTUALIZED_ROW_HEIGHT = 45; // Adjust as needed based on your cell content/padding

const VirtualTable = ({ columns, dataSource, scroll, tableRef }) => {
  const gridRef = useRef();
  const [connectObject] = useState(() => {
    const obj = {};
    Object.defineProperty(obj, 'scrollLeft', {
      get: () => null,
      set: (scrollLeft) => {
        if (gridRef.current) {
          gridRef.current.scrollTo({ scrollLeft });
        }
      },
    });
    return obj;
  });

  const renderVirtualList = (rawData, { scrollbarSize, ref, onScroll }) => {
    ref.current = connectObject;
    const totalHeight = rawData.length * VIRTUALIZED_ROW_HEIGHT;

    return (
      <FixedSizeList
        ref={gridRef}
        className="virtual-grid" // Add a class for potential styling
        height={scroll.y} // Use the height provided by AntD Table scroll prop
        itemCount={rawData.length}
        itemSize={VIRTUALIZED_ROW_HEIGHT}
        width="100%" // Let AntD handle width based on columns
        itemData={rawData} // Pass the raw data
        onScroll={({ scrollOffset, scrollDirection }) => {
          onScroll({ scrollLeft: scrollOffset }); // Map FixedSizeList scroll to AntD
        }}
      >
        {({ index, style }) => {
          const record = rawData[index];
          return (
            <tr 
              key={record.id} 
              style={style} 
              className={`virtual-table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`}
            >
              {columns.map((col) => {
                let cellContent;
                if (col.render) {
                  cellContent = col.render(record[col.dataIndex], record, index);
                } else {
                  cellContent = record[col.dataIndex];
                }
                return (
                  <td 
                    key={col.key} 
                    style={{ 
                      width: col.width, 
                      minWidth: col.width, 
                      maxWidth: col.width,
                      padding: '8px', 
                      borderBottom: '1px solid #f0f0f0',
                      verticalAlign: 'top',
                    }}
                  >
                    {cellContent}
                  </td>
                );
              })}
            </tr>
          );
        }}
      </FixedSizeList>
    );
  };

  return (
    <Table
      ref={tableRef}
      {...scroll}
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      components={{
        body: renderVirtualList,
      }}
      rowKey="id"
      size="small"
      bordered
    />
  );
};
// --- End Virtualized Table Body ---

// --- Aangepaste Autocomplete voor Zowel Server-Side als Client-Side Search ---
const StofAutocomplete = React.memo(({ 
    value, 
    onChange, 
    onGridNavigate, 
    rowId, 
    fieldName, 
    initialId, 
    initialLabel, 
    loading: initialLoading, 
    placeholder = "Typ om te zoeken...", 
    options: providedOptions, 
    useVirtualization = true, 
    analysisStofValue = '', 
    isReadOnly
}) => {
    
    const [searchOptions, setSearchOptions] = useState([]); 
    const [isSearching, setIsSearching] = useState(false); 
    const [inputValue, setInputValue] = useState(''); 
    const [debouncedInputValue, setDebouncedInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false); 
    const [highlightedOption, setHighlightedOption] = useState(null); 

    const isServerSide = providedOptions === undefined;

    const memoizedInitialOption = useMemo(() => {
        if (initialId && initialLabel) {
            if (fieldName === 'parameter') {
                return { domain_value_id: initialId, description: initialLabel };
            } else if (fieldName === 'unit') {
                return { domain_value_id: initialId, aquo_code: initialLabel, description: initialLabel }; 
            }
        }
        return null;
    }, [initialId, initialLabel, fieldName]);

    const getOptionLabel = useCallback((option) => {
        if (typeof option === 'string') {
            return option; 
        }
        if (!option) return '';
        if (isServerSide) return option.description || '';
        return option.aquo_code || option.description || ''; 
    }, [isServerSide]);

    const currentOption = useMemo(() => {
        const optionsToSearch = isServerSide ? searchOptions : providedOptions || [];
        if (value !== null && value !== undefined) {
            let found = optionsToSearch.find(opt => opt && opt.domain_value_id === value); 
            if (found) return found;
            if (memoizedInitialOption && memoizedInitialOption.domain_value_id === value) {
                return memoizedInitialOption; 
            }
        }
        return null; 
    }, [searchOptions, providedOptions, value, isServerSide, memoizedInitialOption]);
    
    useEffect(() => {
        const correctLabel = getOptionLabel(currentOption);
        if (correctLabel !== inputValue) {
            setInputValue(correctLabel);
        }
    }, [currentOption]);

    const debouncedSetQuery = useMemo(
        () => isServerSide ? debounce((newValue) => {
            setDebouncedInputValue(newValue);
        }, 300) : () => {}, 
        [isServerSide] 
    );

    useEffect(() => {
         if (!isServerSide) return; 

         let isMounted = true;
         if (debouncedInputValue.length < 2) {
             setSearchOptions([]); 
             setIsSearching(false);
             return;
         }

         setIsSearching(true);
         searchParameters(debouncedInputValue)
             .then(results => {
                 if (isMounted) {
                     let rawResults = results || [];
                     const searchTerm = debouncedInputValue.toLowerCase();

                     const sortedResults = rawResults.sort((a, b) => {
                         const labelA = getOptionLabel(a)?.toLowerCase() || '';
                         const labelB = getOptionLabel(b)?.toLowerCase() || '';

                         const isAExact = labelA === searchTerm;
                         const isBExact = labelB === searchTerm;
                         const isAStartsWith = labelA.startsWith(searchTerm);
                         const isBStartsWith = labelB.startsWith(searchTerm);

                         if (isAExact && !isBExact) return -1;
                         if (!isAExact && isBExact) return 1;
                         if (isAExact && isBExact) return 0;

                         if (isAStartsWith && isBStartsWith) {
                            return labelA.length - labelB.length;
                         }
                         if (isAStartsWith && !isBStartsWith) return -1;
                         if (!isAStartsWith && isBStartsWith) return 1;

                         return 0;
                     });

                     if (currentOption && !sortedResults.some(opt => opt.domain_value_id === currentOption.domain_value_id)) {
                        sortedResults.unshift(currentOption); 
                     }
                     setSearchOptions(sortedResults);
                 }
             })
             .catch(error => {
                 console.error("Error searching parameters:", error);
                 if (isMounted) {
                     setSearchOptions([]); 
                 }
             })
             .finally(() => {
                 if (isMounted) {
                     setIsSearching(false);
                 }
             });

         return () => { 
             isMounted = false; 
             if (typeof debouncedSetQuery.cancel === 'function') {
                debouncedSetQuery.cancel();
             }
         };
     }, [debouncedInputValue, isServerSide, debouncedSetQuery]);

    const handleInputChange = useCallback((event, newInputValue, reason) => {
        setInputValue(newInputValue);

        if (reason === 'input' && isServerSide) {
            debouncedSetQuery(newInputValue); 
        } else if (reason === 'clear') {
            if (isServerSide) {
                setDebouncedInputValue(''); 
                setSearchOptions([]); 
                if (typeof debouncedSetQuery.cancel === 'function') {
                    debouncedSetQuery.cancel();
                }
            }
            if (value !== null && value !== undefined) {
                 onChange(null);
            }
        }
    }, [isServerSide, debouncedSetQuery, onChange, value]);

    const handleSelectionChange = useCallback((event, newValue) => {
        let newId = null;
        let newOptionForState = null; 

        if (newValue && typeof newValue === 'object' && newValue.domain_value_id) {
            newId = newValue.domain_value_id;
            newOptionForState = newValue; 
            setInputValue(getOptionLabel(newValue));
        } else {
            newId = null;
            newOptionForState = null;
        }

        if (isServerSide) {
            setSearchOptions(newOptionForState ? [newOptionForState] : []);
            if (typeof debouncedSetQuery.cancel === 'function') {
                debouncedSetQuery.cancel();
            }
        }
        
        if (value !== newId) {
             onChange(newId); 
        }
        setIsOpen(false);
        setHighlightedOption(null);
    }, [isServerSide, onChange, debouncedSetQuery, value, getOptionLabel]);

    const handleBlur = useCallback((event) => {
        const currentInputText = event.target.value;
        const correctLabel = getOptionLabel(currentOption);
        
        if (currentInputText !== correctLabel) {
            setInputValue(correctLabel);
            if (isServerSide && currentInputText !== '' && correctLabel === '') {
                setDebouncedInputValue('');
                setSearchOptions([]);
                if (typeof debouncedSetQuery.cancel === 'function') debouncedSetQuery.cancel();
            }
        }
        setIsOpen(false);
        setHighlightedOption(null);
    }, [currentOption, getOptionLabel, isServerSide, debouncedSetQuery]);

    const handleHighlightChange = useCallback((event, option) => {
        setHighlightedOption(option);
    }, []);

    const finalOptions = useMemo(() => {
        let dynamicOptions = isServerSide ? searchOptions : (providedOptions || []);
        if (currentOption && !dynamicOptions.some(opt => opt && opt.domain_value_id === currentOption.domain_value_id)) {
            return [currentOption, ...dynamicOptions.filter(opt => !!opt)];
        }
        return dynamicOptions.filter(opt => !!opt);
    }, [isServerSide, searchOptions, providedOptions, currentOption]);

    const handleKeyDownAutocomplete = useCallback((event) => {
        if (event.key === 'Enter' && isOpen && finalOptions && finalOptions.length > 0) {
             event.preventDefault(); 
             handleSelectionChange(null, finalOptions[0]);
        } else if (event.key === 'Tab' && isOpen && highlightedOption) {
            event.preventDefault(); 
            handleSelectionChange(null, highlightedOption);
        } else {
            if (onGridNavigate) {
                onGridNavigate(event); 
            }
        }
    }, [isOpen, highlightedOption, handleSelectionChange, onGridNavigate, finalOptions]);

    const isLoading = initialLoading || (isServerSide && isSearching); 

    const internalInputProps = useMemo(() => ({
        'data-row-id': rowId,
        'data-field': fieldName,
    }), [rowId, fieldName]);

    const tooltipTitle = analysisStofValue ? `Hint: ${analysisStofValue}` : '';

    return (
        <Autocomplete
            freeSolo
            fullWidth
            disableListWrap={useVirtualization}
            size="small"
            options={finalOptions} 
            loading={isLoading} 
            loadingText={isServerSide ? "Bezig met zoeken..." : "Laden..."}
            noOptionsText={isServerSide ? (inputValue.length < 2 ? "Typ minimaal 2 tekens" : "Geen resultaten") : "Geen opties"}
            getOptionLabel={getOptionLabel}
            value={currentOption}
            onChange={handleSelectionChange}
            onInputChange={handleInputChange}
            inputValue={inputValue}
            isOptionEqualToValue={(option, val) => option?.domain_value_id === val?.domain_value_id}
            filterOptions={isServerSide ? (options) => options : undefined}
            ListboxComponent={useVirtualization ? ListboxComponent : undefined}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => {
                setIsOpen(false);
                setHighlightedOption(null);
            }}
            onHighlightChange={handleHighlightChange}
            disabled={isReadOnly}
            renderInput={(params) => ( 
                <Tooltip title={tooltipTitle} mouseEnterDelay={0.5} getPopupContainer={triggerNode => triggerNode.parentElement}>
                  <span {...(isReadOnly ? { style: { display: 'inline-block', width: '100%' } } : {})} > 
                    <TextField
                        {...params} 
                        variant="standard"
                        onBlur={handleBlur}
                        InputProps={{
                            ...params.InputProps, 
                            disableUnderline: true,
                             endAdornment: ( 
                                <>
                                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </> 
                            ),
                        }}
                        inputProps={{
                            ...params.inputProps, 
                            ...internalInputProps
                        }}
                        onKeyDown={handleKeyDownAutocomplete}
                        placeholder={placeholder}
                    />
                  </span> 
                </Tooltip>
            )}
        />
    );
}, (prevProps, nextProps) => {
     return (
         prevProps.value === nextProps.value &&
         prevProps.options === nextProps.options && 
         prevProps.rowId === nextProps.rowId &&
         prevProps.fieldName === nextProps.fieldName &&
         prevProps.initialId === nextProps.initialId &&
         prevProps.initialLabel === nextProps.initialLabel &&
         prevProps.loading === nextProps.loading &&
         prevProps.placeholder === nextProps.placeholder &&
         prevProps.onGridNavigate === nextProps.onGridNavigate &&
         prevProps.useVirtualization === nextProps.useVirtualization &&
         prevProps.analysisStofValue === nextProps.analysisStofValue &&
         prevProps.isReadOnly === nextProps.isReadOnly
     );
 });

export default function AnalysisTab({ document, isReadOnly }) {
  const { isDarkMode } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [localError, setLocalError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const analysisTableRef = useRef(null);
  const virtualListRef = useRef();

  useEffect(() => {
    return () => {
      if (document?.id) {
        invalidateSampleCache(document.id);
      }
    };
  }, [document?.id]);

  const sampleQueryParams = useMemo(() => ({ document_id: document.id }), [document.id]);
  const { samples: unsortedSamples, loading: samplesLoading, error: samplesError } = useAnalysisSamples(sampleQueryParams);

  const samples = useMemo(() => {
    if (!unsortedSamples) return [];
    return [...unsortedSamples].sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB, 'nl', { numeric: true });
    });
  }, [unsortedSamples]);

  const currentSampleId = useMemo(() => {
    if (samples && samples.length > 0 && currentIndex >= 0 && currentIndex < samples.length) {
      return samples[currentIndex]?.id || null;
    }
    return null;
  }, [samples, currentIndex]);

  const { analyses, loading: analysesLoading, error: analysesError, refetchAnalyses } = useAnalyses(currentSampleId);

  const domainTechnicalNames = useMemo(() => ['Parameter', 'Eenheid'], []);
  const { domainValues, loading: domainValuesLoading, error: domainValuesError } = useDomainValues(domainTechnicalNames);

  useEffect(() => {
      if (samples && currentIndex >= samples.length) {
          setCurrentIndex(Math.max(0, samples.length - 1));
      }
  }, [samples, currentIndex]);

  const debouncedSaveAnalysis = useCallback(
    debounce(async (id, updates, sampleIdForSave) => {
      if (isReadOnly) {
        console.log("Read-only mode: Save prevented for analysis", id);
        return;
      }
      setLocalError(null);
      const isNew = id && typeof id === 'string' && id.startsWith('new-');

      try {
         if (isNew) {
            const { id: tempId, ...dataToCreateRaw } = {
                ...updates,
                sample_id: sampleIdForSave || currentSampleId,
                document_id: document.id
            };

            dataToCreateRaw.id = uuidv4();

            const fieldsToRemove = [
                'parameter_label',
                'uom_label',
                'PhysicalProperty_quantity_value',
                'PhysicalProperty_parameter_value',
                'PhysicalProperty_condition_value',
                'limitSymbol_value',
                'numericvalue_uom_value',
                'valueProcessingMethod_value',
                'raw_relations',
            ];
            const dataToCreate = { ...dataToCreateRaw };
            fieldsToRemove.forEach(field => delete dataToCreate[field]);

            console.log("Creating analysis with data:", dataToCreate);
            await createAnalysis(dataToCreate);
            message.success('Nieuwe analyse succesvol aangemaakt');
            refetchAnalyses();
         } else if (id) {
           await updateAnalysis(id, updates);
         } else {
           console.warn("Debounced save called without a valid ID", { id, updates });
         }
      } catch (error) {
        console.error("Save error:", error);
         const action = isNew ? 'aanmaken' : 'opslaan';
         setLocalError(`Analyse ${action} mislukt: ${error.message}`);
         message.error(`Fout bij ${action} van analyse.`);
      }
    }, 1500),
    [refetchAnalyses, document.id, currentSampleId, isReadOnly]
  );

  const logParameterCorrection = useCallback(async (analysisId, originalStofValue, selectedParameterId) => {
    if (isReadOnly) {
      console.log("Read-only mode: Correction log prevented for analysis", analysisId);
      return;
    }
    if (!analysisId || !selectedParameterId || (typeof analysisId === 'string' && analysisId.startsWith('new-'))) {
      return;
    }

    if (!originalStofValue || originalStofValue.trim() === '') {
      console.log("Skipping correction log: No original stof value provided by AI.");
      return;
    }

    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      return; 
    }

    const currentUserId = user.id;

    const correctionData = {
      analysis_id: analysisId,
      original_stof_value: originalStofValue,
      selected_parameter_id: selectedParameterId,
      corrected_by_user_id: currentUserId,
    };

    const { error: upsertError } = await supabase
      .from('user_stof_correctie')
      .upsert(correctionData, { onConflict: 'analysis_id' }); 

    if (upsertError) {
      console.error("Supabase upsert error logging correction:", upsertError);
    } else {
    }
  }, []);

  const [editableAnalyses, setEditableAnalyses] = useState([]);
  const editableAnalysesRef = useRef(editableAnalyses);

  useEffect(() => {
      if (analyses) {
          const sortedAnalyses = [...analyses].sort((a, b) => {
              const idA = a.auto_increment_id ?? 0; 
              const idB = b.auto_increment_id ?? 0;
              return idA - idB;
          });
          setEditableAnalyses(sortedAnalyses);
          editableAnalysesRef.current = sortedAnalyses;
      } else {
          setEditableAnalyses([]);
          editableAnalysesRef.current = [];
      }
  }, [analyses]);

  const handleAnalysisChange = useCallback((analysisId, field, value) => {
    if (isReadOnly) {
      console.log("Read-only mode: Change prevented for analysis", analysisId, field);
      return;
    }
    setEditableAnalyses(currentAnalyses => {
        let analysisUpdated = false;
        const updatedAnalyses = currentAnalyses.map(analysis => {
            if (analysis.id === analysisId) {
                analysisUpdated = true;
                const previousValue = analysis[field];
                const updatedAnalysis = { ...analysis, [field]: value };

                if (field === 'PhysicalProperty_parameter' && 
                    value !== previousValue && 
                    value !== null && value !== undefined) {
                  const originalStofText = analysis.stof_value || ''; 
                  logParameterCorrection(analysisId, originalStofText, value); 
                }

                const isNewPlaceholder = analysisId && typeof analysisId === 'string' && analysisId.startsWith('new-');
                if (!isNewPlaceholder && analysisId) {
                    debouncedSaveAnalysis(analysisId, { [field]: value }, currentSampleId);
                } else if (isNewPlaceholder) {
                    updatedAnalysis.document_id = document.id;
                    updatedAnalysis.sample_id = currentSampleId; 
                    debouncedSaveAnalysis(analysisId, updatedAnalysis, currentSampleId); 
                }
                return updatedAnalysis;
            }
            return analysis;
        });

        if (!analysisUpdated) {
            console.warn('handleAnalysisChange called but analysisId not found:', analysisId);
            return currentAnalyses;
        }

        return updatedAnalyses;
    });
  }, [debouncedSaveAnalysis, document.id, currentSampleId, logParameterCorrection, isReadOnly]);

  const handleNext = () => {
    if (samples && currentIndex < samples.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSelectChange = (value) => {
    const newIndex = parseInt(value, 10);
    if (newIndex !== currentIndex) {
        setCurrentIndex(newIndex);
    }
  };

  const handleAddAnalysis = async () => {
    if (isReadOnly) {
      message.warning('Kan geen analyse toevoegen in read-only modus.');
      return;
    }
    if (!currentSampleId) {
      message.warning('Selecteer eerst een monster om een analyse aan toe te voegen.');
      return;
    }

    setIsAdding(true);
    setLocalError(null);

    const tempId = `new-${Date.now()}`; 
    
    const newAnalysisRow = {
      id: tempId,
      document_id: document.id,
      sample_id: currentSampleId,
      PhysicalProperty_parameter: null,
      numericvalue: null,
      numericvalue_uom: null,
      phenomenontime: null, 
      resulttime: null,
      procedure: '',
      observedproperty: '',
      valueProcessingMethod: null,
      limitSymbol: '',
      alphanumericValue: '',
      limitSymbolReferenceCode: null,
      PhysicalProperty_quantity: null,
      PhysicalProperty_condition: null,
      PhysicalProperty_quantity_value: '',
      PhysicalProperty_parameter_value: '',
      PhysicalProperty_condition_value: '',
      limitSymbol_value: '',
      numericvalue_uom_value: '',
      valueProcessingMethod_value: ''
    };

    const newEditableAnalyses = [...editableAnalyses, newAnalysisRow];
    setEditableAnalyses(newEditableAnalyses);
    editableAnalysesRef.current = newEditableAnalyses;
    
    const newRowIndex = newEditableAnalyses.length - 1;
    if (virtualListRef.current && newRowIndex >= 0) {
        requestAnimationFrame(() => { 
            if (virtualListRef.current) { 
               virtualListRef.current.scrollToItem(newRowIndex, 'smart');
            }
        }); 
    }

    setIsAdding(false); 
    message.info('Lege analyse rij toegevoegd. Vul de gegevens in.');
  };

  const handleDeleteAnalysis = async (analysisId) => {
    setLocalError(null);
    
    if (isReadOnly) {
      message.warning('Kan geen analyse verwijderen in read-only modus.');
      return;
    }

    setEditableAnalyses(prevAnalyses => prevAnalyses.filter(a => a.id !== analysisId));

    if (typeof analysisId === 'string' && analysisId.startsWith('new-')) {
        message.success('Nieuwe rij verwijderd.');
        debouncedSaveAnalysis.cancel();
        return; 
    }

    try {
      await deleteAnalysis(analysisId);
      message.success('Analyse succesvol verwijderd');
    } catch (error) {
      console.error("Delete error:", error);
      setLocalError(`Verwijderen van analyse mislukt: ${error.message}`);
      message.error('Fout bij verwijderen analyse.');
      refetchAnalyses(); 
    }
  };

  const filteredEenheidOptions = useMemo(() => {
      const allowedUnitCodes = ['%', 'mg/kg', 'µg/kg','µg/L'];
      const eenheden = domainValues['Eenheid'] || [];
      return allowedUnitCodes
          .map(code => eenheden.find(opt => opt.aquo_code === code))
          .filter(Boolean);
  }, [domainValues]);

  const handleKeyDown = useCallback((e, currentField, currentRowId) => {
      const analysesForCurrentSample = editableAnalysesRef.current || []; 
      const currentRowIndex = analysesForCurrentSample.findIndex(a => a.id === currentRowId);

      if (currentRowIndex === -1 || e.key !== 'Tab' || e.shiftKey) return;

      e.preventDefault();

      let nextField = '';
      let nextRowIndex = currentRowIndex;
      let focusSelector = '';
      let nextElement = null;

      if (currentField === 'parameter') {
        nextField = 'value';
      } else if (currentField === 'value') {
        nextField = 'unit';
      } else if (currentField === 'unit') {
        nextField = 'parameter';
        nextRowIndex++;
      }

      if (nextRowIndex < analysesForCurrentSample.length) {
        const nextRowId = analysesForCurrentSample[nextRowIndex].id;
        focusSelector = `[data-row-id="${nextRowId}"][data-field="${nextField}"]`;
      } else if (currentField === 'unit' && nextRowIndex >= analysesForCurrentSample.length) {
        nextElement = document.getElementById('add-analysis-button');
      }

      if (focusSelector) {
           const targetWrapper = analysisTableRef.current?.querySelector(focusSelector);
           if (targetWrapper) {
               nextElement = targetWrapper.querySelector('input, textarea');
               if (!nextElement && targetWrapper.matches('input, textarea')) {
                   nextElement = targetWrapper;
               }
           }
      } 

      if (nextElement) {
        nextElement.focus();
        if (nextElement.select && (nextField === 'value' || nextField === 'parameter' || nextField === 'unit')) { 
            nextElement.select();
        }
      } else {
        console.log("Next element not found for", nextField, nextRowIndex);
      }
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Stof',
      dataIndex: 'PhysicalProperty_parameter',
      key: 'parameter',
      width: 250,
      render: (parameterId, record, index) => { 
        return (
            <StofAutocomplete
              value={parameterId}
              className="compact-autocomplete"
              loading={domainValuesLoading}
              onChange={(newValueId) => {
                handleAnalysisChange(record.id, 'PhysicalProperty_parameter', newValueId);
              }}
              onGridNavigate={(e) => handleKeyDown(e, 'parameter', record.id)}
              rowId={record.id}
              fieldName="parameter"
              initialId={parameterId}
              initialLabel={record.parameter_label || ''}
              placeholder="Selecteer stof..."
              analysisStofValue={record.stof_value || ''}
              isReadOnly={isReadOnly}
          />
        );
      },
    },
    {
      title: 'Waarde',
      dataIndex: 'alphanumericValue',
      key: 'value',
      width: 120,
      render: (value, record, index) => (
        <Input
          style={{ width: '100%' }}
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            const sanitizedValue = newValue.replace(/,/g, '.');
            if (
              sanitizedValue === '' || 
              sanitizedValue === '-' || 
              sanitizedValue === '<' || 
              sanitizedValue === '>' ||
              /^[<>]?-?\d*\.?\d*$/.test(sanitizedValue)
            ) {
              let finalValue = sanitizedValue;
              if (sanitizedValue.startsWith('-') && sanitizedValue.length > 1) {
                finalValue = '<' + sanitizedValue.substring(1);
              }
              handleAnalysisChange(record.id, 'alphanumericValue', finalValue === '' ? null : finalValue);
            }
          }}
          placeholder="Voer waarde in"
          onKeyDown={(e) => {
            if (
              e.key === 'Backspace' ||
              e.key === 'Delete' ||
              e.key === 'Tab' ||
              e.key === 'Escape' ||
              e.key === 'Enter' ||
              e.key === '.' ||
              e.key === ',' ||
              e.key === '-' ||
              e.key === '<' ||
              e.key === '>' ||
              e.key === 'ArrowLeft' ||
              e.key === 'ArrowRight' ||
              /^\d$/.test(e.key)
            ) {
              if ((e.key === '<' || e.key === '>') && e.target.value !== '' && e.target.selectionStart !== 0) {
                e.preventDefault();
                return;
              }
              handleKeyDown(e, 'value', record.id);
            } else {
              e.preventDefault();
            }
          }}
          id={`input-${record.id}-value`}
          data-row-id={record.id}
          data-field="value"
          disabled={isReadOnly}
        />
      ),
    },
    {
      title: 'Eenheid',
      dataIndex: 'numericvalue_uom',
      key: 'unit',
      width: 150,
      render: (unitId, record, index) => {
        return (
              <StofAutocomplete
                value={unitId}
                options={filteredEenheidOptions}
                className="compact-autocomplete"
                loading={domainValuesLoading}
                onChange={(newValueId) => handleAnalysisChange(record.id, 'numericvalue_uom', newValueId)}
                onGridNavigate={(e) => handleKeyDown(e, 'unit', record.id)}
                rowId={record.id}
                fieldName="unit"
                initialId={unitId}
                initialLabel={record.uom_label || ''}
                placeholder="Selecteer eenheid..."
                useVirtualization={false}
                isReadOnly={isReadOnly}
              />
          );
       },
    },
    {
      title: 'Acties',
      key: 'action',
      fixed: 'right',
      width: 60,
      align: 'center',
      render: (_, record, index) => {
        return (
          <Button 
             onClick={() => {
                handleDeleteAnalysis(record.id);
             }}
             type="text" 
             danger 
             icon={<DeleteOutlined />} 
             size="small" 
             title="Verwijder deze analyse (direct)"
             data-row-id={record.id} 
             data-action="delete"
             disabled={isReadOnly}
          />
        );
      },
    },
  ], [filteredEenheidOptions, domainValuesLoading, handleAnalysisChange, handleDeleteAnalysis, handleKeyDown, isReadOnly]);

  const loading = samplesLoading || (currentSampleId && analysesLoading) || domainValuesLoading;
  const error = samplesError || analysesError || domainValuesError || localError;
  const fetchError = samplesError || analysesError || domainValuesError;

  const currentAntdAlgorithm = isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

  if (samplesLoading && !samples) {
    return (
      <ConfigProvider theme={{ algorithm: currentAntdAlgorithm }}>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <Spin size="large" />
                <p>Monsters laden...</p>
        </div>
      </ConfigProvider>
    );
  }

  if (fetchError && !localError) {
    return (
      <ConfigProvider theme={{ algorithm: currentAntdAlgorithm }}>
        <Alert
                message="Fout bij ophalen gegevens"
                description={`${fetchError}`}
          type="error"
          showIcon
        />
      </ConfigProvider>
    );
  }

  const sampleOptions = Array.isArray(samples) ? samples : [];

  return (
    <ConfigProvider theme={{
        algorithm: currentAntdAlgorithm,
      }}>
      <Card style={{ margin: '20px 0' }}>
        {localError && (
          <Alert 
            message="Fout"
            description={localError} 
            type="warning"
            showIcon 
            closable 
            onClose={() => setLocalError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row justify="space-between" align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12}>
            <Space direction="horizontal">
              <Button icon={<LeftOutlined />} onClick={handlePrev} disabled={currentIndex === 0 || sampleOptions.length === 0} />
              <Select
                id="sample-select"
                value={sampleOptions.length > 0 ? currentIndex.toString() : undefined}
                onChange={handleSelectChange}
                style={{ minWidth: 150, maxWidth: 300 }}
                disabled={sampleOptions.length === 0}
                loading={samplesLoading && sampleOptions.length > 0}
                getPopupContainer={triggerNode => triggerNode.parentNode}
                notFoundContent={samplesLoading ? <Spin size="small" /> : "Geen monsters gevonden"}
              >
                {sampleOptions.map((sample, index) => (
                  <Option key={sample.id} value={index.toString()}>
                    {sample.name || `Sample ID: ${sample.id.substring(0, 8)}...`}
                  </Option>
                ))}
              </Select>
              <Button icon={<RightOutlined />} onClick={handleNext} disabled={sampleOptions.length === 0 || currentIndex >= sampleOptions.length - 1} />
            </Space>
          </Col>
          <Col xs={24} sm={8} md={12} style={{ textAlign: 'right' }}>
            <Title level={4} style={{ marginBottom: 0, marginLeft: 'auto' }}>
               Analyses voor: {sampleOptions[currentIndex]?.name || (currentSampleId ? `Monster ${currentSampleId.substring(0,4)}...` : 'Selecteer Monster')}
            </Title>
          </Col>
        </Row>

        <div className="analysis-table-container" style={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <VirtualTable
            columns={columns}
            dataSource={editableAnalyses}
            scroll={{ y: 500, x: true }}
            tableRef={analysisTableRef}
          />
          <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddAnalysis}
              disabled={!currentSampleId || isAdding || analysesLoading || isReadOnly}
              loading={isAdding}
              id="add-analysis-button"
              style={{ marginTop: 16 }}
          >
              Analyse Toevoegen
          </Button>
        </div>
        
        {!samplesLoading && sampleOptions.length === 0 && (
            <Text>Geen monsters gevonden voor dit document.</Text>
        )}
      </Card>
    </ConfigProvider>
  );
}

// --- CSS for compact dropdowns --- 
const styles = `
.compact-autocomplete .MuiInputBase-root {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  padding-left: 6px !important; /* Adjust as needed */
  padding-right: 26px !important; /* Adjust space for dropdown arrow */
  min-height: 28px; /* Adjust based on desired height */
}

.compact-autocomplete .MuiInputBase-input {
  padding: 4px 0px !important; /* Adjust vertical padding inside input */
  font-size: 0.9em !important; /* Optional: slightly smaller font */
}

.virtual-grid {
  /* Optional: Add styles for the virtual grid container */
}

.virtual-table-row {
  display: flex; /* Use flex for row layout */
  align-items: stretch; /* Stretch cells vertically */
  width: 100% !important; /* Ensure row takes full width */
}

.virtual-table-row td {
  /* Ensure cells behave correctly in flex layout */
  display: flex;
  align-items: center; /* Center content vertically */
  box-sizing: border-box; /* Include padding/border in width */
  overflow: hidden; /* Prevent content overflow */
  text-overflow: ellipsis; /* Add ellipsis if content overflows */
  white-space: nowrap; /* Keep content on one line */
}

/* Optional: Zebra striping */
.virtual-table-row.odd-row {
  background-color: #fafafa;
}
.virtual-table-row.even-row {
  background-color: #ffffff;
}

/* Adjustments for dark mode if needed */
body.dark-mode .virtual-table-row.odd-row {
  background-color: #1f1f1f; /* Example dark mode color */
}
body.dark-mode .virtual-table-row.even-row {
  background-color: #141414; /* Example dark mode color */
}
body.dark-mode .virtual-table-row td {
  border-bottom: 1px solid #303030; /* Darker border */
}
`;

if (typeof window !== 'undefined') {
  let styleSheet = document.getElementById('compact-analysis-styles');
  if (!styleSheet) {
    styleSheet = document.createElement('style');
    styleSheet.id = 'compact-analysis-styles';
    styleSheet.type = 'text/css';
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
  }
}

